/**
 * TodayBookingFilters — Iter 76.
 *
 * Quick-access booking filters for the Admin + Vendor dashboards:
 * Today Reservations · Pickups Today · Returns Today · Overdue Returns.
 * Counts and matching use the Albania (Europe/Tirane) local date and roll
 * over automatically at midnight. Clicking an active filter clears it.
 */
import { useEffect, useState } from "react";
import { CalendarCheck2, LogIn, LogOut, AlarmClockOff } from "lucide-react";

export const ALBANIA_TZ = "Europe/Tirane";

export function tiranaDateStr(d = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: ALBANIA_TZ, year: "numeric", month: "2-digit", day: "2-digit",
  }).format(d);
}

const INACTIVE_STATUSES = new Set(["cancelled", "canceled", "no_show", "declined_by_host", "expired_no_response"]);

export function matchesTodayFilter(b, key, today) {
  const start = String(b.start_date || "").slice(0, 10);
  const end = String(b.end_date || "").slice(0, 10);
  switch (key) {
    case "today": {
      const created = b.created_at ? tiranaDateStr(new Date(b.created_at)) : "";
      const activeToday = !INACTIVE_STATUSES.has(b.status) && start && end && start <= today && today <= end;
      return created === today || activeToday;
    }
    case "pickups":
      return !INACTIVE_STATUSES.has(b.status) && start === today;
    case "returns":
      return !INACTIVE_STATUSES.has(b.status) && end === today;
    case "overdue":
      return b.status === "confirmed" && !!end && end < today;
    default:
      return true;
  }
}

export function applyTodayFilter(bookings, key) {
  if (!key) return bookings;
  const today = tiranaDateStr();
  return (bookings || []).filter((b) => matchesTodayFilter(b, key, today));
}

const FILTERS = [
  {
    key: "today", label: "Today Reservations", short: "Today", icon: CalendarCheck2,
    active: "bg-blue-600 text-white border-blue-600 shadow-sm",
    idle: "bg-white text-blue-700 border-blue-200 hover:bg-blue-50",
    badgeActive: "bg-white/25 text-white", badgeIdle: "bg-blue-100 text-blue-700",
  },
  {
    key: "pickups", label: "Pickups Today", short: "Pickups", icon: LogIn,
    active: "bg-emerald-600 text-white border-emerald-600 shadow-sm",
    idle: "bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50",
    badgeActive: "bg-white/25 text-white", badgeIdle: "bg-emerald-100 text-emerald-700",
  },
  {
    key: "returns", label: "Returns Today", short: "Returns", icon: LogOut,
    active: "bg-amber-500 text-white border-amber-500 shadow-sm",
    idle: "bg-white text-amber-700 border-amber-200 hover:bg-amber-50",
    badgeActive: "bg-white/25 text-white", badgeIdle: "bg-amber-100 text-amber-700",
  },
  {
    key: "overdue", label: "Overdue Returns", short: "Overdue", icon: AlarmClockOff,
    active: "bg-rose-600 text-white border-rose-600 shadow-sm",
    idle: "bg-white text-rose-700 border-rose-200 hover:bg-rose-50",
    badgeActive: "bg-white/25 text-white", badgeIdle: "bg-rose-100 text-rose-700",
    pulseWhenNonZero: true,
  },
];

export default function TodayBookingFilters({ bookings = [], active, onChange, className = "" }) {
  const [today, setToday] = useState(tiranaDateStr());
  useEffect(() => {
    const t = setInterval(() => setToday(tiranaDateStr()), 60_000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`} data-testid="today-booking-filters">
      {FILTERS.map((f) => {
        const count = bookings.reduce((n, b) => n + (matchesTodayFilter(b, f.key, today) ? 1 : 0), 0);
        const isActive = active === f.key;
        const Icon = f.icon;
        return (
          <button
            key={f.key}
            type="button"
            onClick={() => onChange(isActive ? null : f.key)}
            aria-pressed={isActive}
            title={`${f.label} (${count}) — Albania time`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${isActive ? f.active : f.idle} ${f.pulseWhenNonZero && count > 0 && !isActive ? "ring-2 ring-rose-200" : ""}`}
            data-testid={`booking-filter-${f.key}`}
          >
            <Icon className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{f.label}</span>
            <span className="sm:hidden">{f.short}</span>
            <span
              className={`min-w-[1.25rem] px-1 py-0.5 rounded-full text-[10px] font-bold tabular-nums text-center ${isActive ? f.badgeActive : f.badgeIdle}`}
              data-testid={`booking-filter-${f.key}-count`}
            >
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );
}
